/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';

import styles from './styles.module.scss';
import ActionButtons from '../../components/ActionButtons';
import DataLoader from '../../components/DataLoader';
import PriceSummaryRow from '../../components/PriceSummaryRow';
import ScrollableContent from '../../components/ScrollableContent';
import Sidebar from '../../components/Sidebar';
import Table from '../../components/Table';
import TrackListHeader from '../../components/TrackListHeader';
import TrackListRow from '../../components/TrackListRow';
import actions from '../../redux/actions';
import { getIsPlaying } from '../../redux/reducers/playerQueue';
import { getTrackSearchParams } from '../../redux/reducers/trackSearch';
import { getWorklists } from '../../redux/reducers/worklists';
import liferay from '../../services/liferay';
import protonesUtils from '../../utils/protones';
import stringUtils from '../../utils/string';

import type {
  ReactRouterProps,
  Translate,
  Fetchable,
  SimplifiedMedia,
  Purchase,
  Worklist,
  ReduxState,
  SearchParams,
  TrackSearchParams,
} from '../../types';

const mapStateToProps = (state: ReduxState, ownProps) => {
  return {
    worklistList: getWorklists(state),
    currentTrackId: state.playerQueue.currentTrackId,
    isPlaying: getIsPlaying(state),
    loadingItems: state.cart.cartItemsBeingDeleted,
    trackSearchParams: getTrackSearchParams(state),
  };
};

type Props = ReactRouterProps & {
  worklistList: Fetchable<Worklist[]>,
  fetchWorklistList: typeof actions.fetchWorklistList,
  loadingItems: number[],
  currentTrackId: number,
  isPlaying: boolean,
  playTracks: typeof actions.playTracks,
  downloadTracks: typeof actions.downloadTracks,
  askQuotation: typeof actions.askQuotation,
  performTrackSearch: typeof actions.performTrackSearch,
  trackSearchParams: TrackSearchParams,
  displayMessage: typeof actions.displayMessage,
  t: Translate,
};

type State = {
  selectedComponentsByItem: { [itemId: string]: string[] },
  items: Fetchable<Purchase[]>,
  expandedItemsByWorklist: {
    [worklistId: number]: {
      [itemId: number]: boolean,
    },
  },
};

class Purchases extends React.Component<Props, State> {
  state = {
    selectedComponentsByItem: {},
    items: { fetching: false, error: undefined, data: undefined },
    expandedItemsByWorklist: {},
  };

  get expandedItemIdsByWorklist(): { [worklistId: number]: number[] } {
    return _.mapValues(this.state.expandedItemsByWorklist, expandedItems =>
      _.map(_.keys(_.pickBy(expandedItems, v => v === true)), v => Number(v))
    );
  }

  _toggleExpandSelectedItemsForWorklist = (worklistId: number) => {
    const { selectedComponentsByItem } = this.state;
    const items = (this.state.items.data || []).filter(
      item => item.worklistItem.worklistId === worklistId
    );
    const expandedItems = this.state.expandedItemsByWorklist[worklistId] || {};
    const selectedItems = items.filter(item => selectedComponentsByItem.hasOwnProperty(item.id));
    const selectedItemIds = selectedItems.map(i => i.id);
    const isAllExpanded = selectedItemIds.every(id =>
      (this.expandedItemIdsByWorklist[worklistId] || []).includes(id)
    );
    for (const itemId of selectedItemIds) {
      expandedItems[itemId] = !isAllExpanded;
    }
    this.setState({
      expandedItemsByWorklist: {
        ...this.state.expandedItemsByWorklist,
        [worklistId]: expandedItems,
      },
    });
  };

  async _fetchItems() {
    this.setState({ items: { fetching: true } });
    try {
      const data = await liferay.purchases.find();

      this.setState({ items: { fetching: false, data } });
    } catch (error) {
      console.error(error);
      this.setState({ items: { fetching: false, error } });
    }
  }

  componentDidMount() {
    const { fetchWorklistList, worklistList } = this.props;
    if (!worklistList.data && !worklistList.fetching) {
      fetchWorklistList();
    }
    this._fetchItems();
  }

  _toggleSelectAllTracks = (itemIds: string[]) => {
    const { selectedComponentsByItem } = this.state;
    const { items } = this.state;
    const isSelectedAll = itemIds.every(id => selectedComponentsByItem.hasOwnProperty(id));
    if (isSelectedAll) {
      const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
      itemIds.forEach(id => {
        delete nextSelectedComponentsByItem[id];
      });
      this.setState({ selectedComponentsByItem: nextSelectedComponentsByItem });
    } else {
      const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
      (items.data || []).forEach(item => {
        if (!itemIds.includes(item.id)) {
          return;
        }
        nextSelectedComponentsByItem[item.id] = (item.worklistItem.mergedComponents || []).map(
          component => component.id
        );
      });
      this.setState({ selectedComponentsByItem: nextSelectedComponentsByItem });
    }
  };

  _toggleSelectTrack = (itemId: string) => {
    const { items } = this.state;
    const { selectedComponentsByItem } = this.state;
    const item = (items.data || []).find(item => item.id === itemId);
    const itemComponentIds = ((item && item.worklistItem.mergedComponents) || []).map(
      component => component.id
    );
    const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
    if (selectedComponentsByItem.hasOwnProperty(itemId)) {
      delete nextSelectedComponentsByItem[itemId];
    } else {
      nextSelectedComponentsByItem[itemId] = itemComponentIds;
    }
    this.setState(state => ({ ...state, selectedComponentsByItem: nextSelectedComponentsByItem }));
  };

  _handleToggleComponent = (componentId: string, itemId: string) => {
    const { selectedComponentsByItem } = this.state;
    const itemComponentIds = selectedComponentsByItem[itemId] || [];
    const selectedComponentIds = itemComponentIds.includes(componentId)
      ? itemComponentIds.filter(id => id !== componentId)
      : [...itemComponentIds, componentId];
    const nextSelectedComponentsByItem = Object.assign({}, selectedComponentsByItem);
    if (selectedComponentIds.length > 0) {
      nextSelectedComponentsByItem[itemId] = selectedComponentIds;
    } else {
      delete nextSelectedComponentsByItem[itemId];
    }
    this.setState(state => ({ ...state, selectedComponentsByItem: nextSelectedComponentsByItem }));
  };

  _handleMediaClick = (media: SimplifiedMedia) => {
    // $FlowFixMe
    protonesUtils.goTo(this.props, protonesUtils.routes.medias.get(media.id));
  };

  _handleRefreshPurchases = () => {
    this._fetchItems();
  };

  _handleTableTitleClick = worklistId => {
    protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklistId));
  };

  _handleTrackSearch = (params: SearchParams) => {
    const { performTrackSearch, trackSearchParams } = this.props;
    performTrackSearch(_.merge({}, trackSearchParams, params));
    protonesUtils.goTo(this.props, protonesUtils.routes.tracks.find());
  };

  _renderActionButtons = (items: Purchase[]) => {
    const { downloadTracks, displayMessage, t } = this.props;
    const tracks = items.map(item => item.worklistItem.track);
    const downloadable = tracks.every(track => track.downloadable === true);
    return (
      <ActionButtons
        worklist={
          items.length === 1
            ? {
                fetching: false,
                data: () => {
                  const item = items[0];
                  const worklistId = item.worklistItem.worklistId;
                  protonesUtils.goTo(this.props, protonesUtils.routes.worklists.get(worklistId));
                },
              }
            : undefined
        }
        download={{
          fetchable: downloadable,
          fetching: false,
          data: () =>
            downloadable
              ? downloadTracks(tracks)
              : displayMessage(t('TRACK_NOT_DOWNLOADABLE'), false),
        }}
      />
    );
  };

  _renderTracklistRows = (data: Purchase[]) => {
    const { loadingItems, currentTrackId, isPlaying, playTracks, worklistList, t } = this.props;
    const { selectedComponentsByItem } = this.state;
    const selectedItems = data.filter(item => selectedComponentsByItem.hasOwnProperty(item.id));
    const dataByWorklistId = _.groupBy(data, item =>
      item ? (item.worklistItem ? item.worklistItem.worklistId : undefined) : undefined
    );
    const worklistIds = Object.keys(dataByWorklistId)
      .filter(worklistId => worklistId != null)
      .map(id => Number(id));
    // Sort worklists by modified date in descending order
    const orderedWorklistIds = _.reverse(
      _.sortBy(worklistIds, worklistId => _.maxBy(dataByWorklistId[worklistId], 'modifiedDate'))
    );
    return orderedWorklistIds.map(worklistId => {
      const itemsOfWorklist = data.filter(item => item.worklistItem.worklistId === worklistId);
      const itemsOfWorklistIds = itemsOfWorklist.map(item => item.id);
      const selectedItemsOfWorklist = selectedItems.filter(
        item => item.worklistItem.worklistId === worklistId
      );
      const isWorklistAllSelected =
        selectedItemsOfWorklist &&
        itemsOfWorklist.length === selectedItemsOfWorklist.length &&
        selectedItemsOfWorklist.length > 0
          ? true
          : false;
      const worklist = _.find(worklistList.data, worklist => worklist.id === worklistId);
      const worklistTitle =
        worklist && worklist.name ? worklist.name : this.props.t('WORKLIST') + ' ' + worklistId;
      const selectedTracks = selectedItemsOfWorklist.map(item => item.worklistItem.track);
      const isSelectedAllExpanded = selectedItemsOfWorklist.every(item =>
        (this.expandedItemIdsByWorklist[worklistId] || []).includes(item.id)
      );

      const worklistItemsDiscountedPrice = itemsOfWorklist.reduce((acc, item) => {
        const itemComponents = item.worklistItem.mergedComponents || [];
        const itemPrice = protonesUtils.getLicenseComponentsTotalDiscountedPrice(itemComponents);
        return acc + itemPrice;
      }, 0);

      return (
        <div className={styles.tracksTable} key={worklistId}>
          <div
            className={styles.tracksTableTitle}
            onClick={this._handleTableTitleClick.bind(this, worklistId)}
          >
            {worklistTitle}
          </div>
          <Table.Table>
            <TrackListHeader
              isSelectable={true}
              showCheckbox={true}
              isSelected={isWorklistAllSelected}
              isPlayerVisible={selectedTracks.length > 0}
              onPlayClick={() => playTracks(selectedTracks)}
              onDetailsExpand={() => this._toggleExpandSelectedItemsForWorklist(worklistId)}
              isDetailsExpanded={isSelectedAllExpanded}
              onCheckboxClick={this._toggleSelectAllTracks.bind(this, itemsOfWorklistIds)}
              showActionButtons={selectedItemsOfWorklist.length > 0}
              showPurchaseDate={true}
              actionButtons={this._renderActionButtons(selectedItems)}
              hasUsage={true}
            />
            {dataByWorklistId[worklistId].map((item, index) => {
              // const isSelected: boolean = selectedComponentsByItem.hasOwnProperty(item.id);
              const selectedComponentIds = selectedComponentsByItem[item.id] || [];
              const components = item.worklistItem.mergedComponents || [];
              const hasAllComponentsSelected =
                components.length > 0 && components.every(c => selectedComponentIds.includes(c.id));
              const hasSomeComponentsSelected =
                !hasAllComponentsSelected &&
                components.some(c => selectedComponentIds.includes(c.id));
              const isLoading: boolean = _.includes(loadingItems, item.id);
              const repricedTrack = item.worklistItem.track;
              repricedTrack.price = item.price;
              const isExpanded = !!_.get(
                this.state.expandedItemsByWorklist,
                `${worklistId}.${item.id}`
              );
              return (
                <TrackListRow
                  key={`${index}-${item.id}`}
                  track={repricedTrack}
                  showPurchaseDate={true}
                  purchaseDate={item.modifiedDate}
                  medias={repricedTrack.medias}
                  usage={item.worklistItem.usage}
                  isReadOnlyUsage={true}
                  isSelectable={true}
                  isSelected={hasAllComponentsSelected}
                  isSelectedIndeterminate={hasSomeComponentsSelected}
                  isCurrentTrack={currentTrackId === item.worklistItem.track.id}
                  isPlaying={isPlaying}
                  isLoading={isLoading}
                  isDetailsExpanded={isExpanded}
                  onMediaClick={this._handleMediaClick}
                  onPlayClick={() => playTracks([item.worklistItem.track])}
                  onPerformerClick={person =>
                    this._handleTrackSearch({
                      text: person.publicName,
                      flags: { people: true },
                    })
                  }
                  onAuthorClick={person =>
                    this._handleTrackSearch({
                      text: person.publicName,
                      flags: { people: true },
                    })
                  }
                  onCheckboxClick={() => this._toggleSelectTrack(item.id)}
                  onComponentCheckboxClick={componentId =>
                    this._handleToggleComponent(componentId, item.id)
                  }
                  onComponentHeaderCheckboxClick={() => this._toggleSelectTrack(item.id)}
                  components={components}
                  selectedComponents={selectedComponentIds}
                  actionButtons={this._renderActionButtons([item])}
                />
              );
            })}
          </Table.Table>
          <PriceSummaryRow
            description={t('TOTAL_PRICE_PURCHASED_ELEMENTS')}
            value={stringUtils.prettifyMoney(worklistItemsDiscountedPrice)}
            isFirstRow={true}
          />
        </div>
      );
    });
  };

  _renderPurchaseData = (data: Purchase[]) => {
    const { t } = this.props;
    const itemsCount = data.length;
    return (
      <div className={styles.container}>
        {itemsCount === 0 ? (
          <div className={styles.centeredContent}>
            <h3 className={styles.title}>{t('NO_PURCHASES')}</h3>
          </div>
        ) : (
          this._renderTracklistRows(data)
        )}
      </div>
    );
  };

  render() {
    const { t } = this.props;
    const {
      items: { fetching, error, data },
    } = this.state;
    return (
      <div className={styles.root}>
        <Sidebar
          title={t('PURCHASE_HISTORY')}
          onRefresh={this._handleRefreshPurchases}
          loading={fetching}
        />
        <ScrollableContent>
          <DataLoader
            loading={fetching}
            error={error}
            data={data}
            render={this._renderPurchaseData}
          />
        </ScrollableContent>
      </div>
    );
  }
}

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(Purchases)));
